import React, { FC, MouseEvent, ReactNode, useState } from 'react';
import { Box, Chip, Fab, Menu, MenuItem, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import { useTranslation } from 'react-i18next';

export const BATCH_SELECTION_LIMIT = 50;

export type BatchAction = {
    icon: ReactNode;
    name: string;
    type: string;
    disabled?: boolean;
};

type BatchActionBarProps = {
    selectedCount: number;
    limit?: number;
    actions: BatchAction[];
    onActionClick: (type: string) => void;
    onClearAll: () => void;
};

const BatchActionBar: FC<BatchActionBarProps> = ({
    selectedCount,
    limit = BATCH_SELECTION_LIMIT,
    actions,
    onActionClick,
    onClearAll,
}: BatchActionBarProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const isAtLimit = selectedCount >= limit;
    const isNearLimit = selectedCount >= Math.floor(limit * 0.8);

    const countChipSx = isAtLimit
        ? {
              bgcolor: 'rgba(254, 201, 201, 0.93)',
              color: '#c83535ff',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              '& .MuiChip-icon': { color: 'inherit' },
              fontWeight: 'bold',
          }
        : isNearLimit
          ? {
                bgcolor: 'rgba(253, 219, 190, 0.91)',
                color: '#e65100',
                border: '1px solid rgba(237, 108, 2, 0.3)',
                '& .MuiChip-icon': { color: 'inherit' },
                fontWeight: 'bold',
            }
          : {
                bgcolor: 'rgba(193, 249, 196, 0.89)',
                color: '#2e7d32',
                border: '1px solid rgba(46, 125, 50, 0.3)',
                '& .MuiChip-icon': { color: 'inherit' },
                fontWeight: 'bold',
            };

    const countChipIcon = isAtLimit ? (
        <ErrorOutlineIcon fontSize="small" />
    ) : isNearLimit ? (
        <WarningAmberIcon fontSize="small" />
    ) : (
        <CheckCircleOutlineIcon fontSize="small" />
    );

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleActionClick = (type: string) => {
        handleClose();
        onActionClick(type);
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ position: 'fixed', left: 100, bottom: 10, zIndex: 100 }}
        >
            <Fab
                variant="extended"
                color="primary"
                aria-controls={open ? 'batch-action-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                disabled={selectedCount === 0}
            >
                {translate('orders:START_BATCH_ACTION_FOR_SELECTED_ORDERS')}
            </Fab>

            <Box sx={{ position: 'relative' }}>
                <Chip
                    icon={countChipIcon}
                    label={`${selectedCount} / ${limit}`}
                    sx={{
                        ...countChipSx,
                        height: 40,
                        fontSize: '0.9rem',
                        '& .MuiChip-icon': {
                            fontSize: '1.2rem',
                            color: 'inherit',
                        },
                    }}
                />
                {isAtLimit && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{
                            position: 'absolute',
                            bottom: 'calc(100% + 4px)',
                            left: 6,
                            lineHeight: 1.25,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {translate('orders:BATCH_SELECTION_LIMIT_REACHED')}
                    </Typography>
                )}
            </Box>

            <Chip
                color="primary"
                icon={<PlaylistRemoveIcon />}
                onClick={onClearAll}
                label={translate('orders:CLEAR_ALL')}
                sx={{
                    height: 40,
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    '& .MuiChip-icon': { fontSize: '1.2rem', color: 'inherit' },
                }}
                clickable
            />

            <Menu
                id="batch-action-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'batch-action-button',
                }}
            >
                {actions.map((action) => (
                    <MenuItem
                        disabled={action.disabled}
                        key={action.type}
                        onClick={() => handleActionClick(action.type)}
                    >
                        {action.name}
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    );
};

export default BatchActionBar;
